/**
 * ActivityFeed — last-24h cross-domain event stream (v0.12.5).
 *
 * Renders CAPA / Deviation / OOS / Audit-finding rows sorted by
 * timestamp in a single timeline panel. Each row deep-links to the
 * source detail page.
 */

import { Link } from '@tanstack/react-router';
import { useActivityFeed, type ActivityFeedItem } from '../../api/orgHooks';
import styles from './HomeAnalytics.module.css';

const KIND_LABEL: Record<ActivityFeedItem['kind'], string> = {
  CAPA: 'CAPA',
  DEVIATION: 'Deviation',
  OOS: 'OOS',
  AUDIT_FINDING: 'Audit finding',
};

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const days = Math.floor(diff / 86_400_000);
  if (days >= 7) return `${Math.floor(days / 7)}w ago`;
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.floor(diff / 60_000);
  return mins <= 0 ? 'just now' : `${mins}m ago`;
}

function linkFor(item: ActivityFeedItem): string {
  switch (item.kind) {
    case 'CAPA':
      return `/capa/${item.id}`;
    case 'DEVIATION':
      return `/deviations/${item.id}`;
    case 'OOS':
      return `/oos/${item.id}`;
    case 'AUDIT_FINDING':
      return `/internal-audits`;
    default:
      return '#';
  }
}

export function ActivityFeed() {
  const q = useActivityFeed();
  const items = q.data?.items ?? [];

  return (
    <section className={styles.feedPanel} aria-label="Recent quality activity">
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Recent activity</h3>
        <span className={styles.panelSub}>Last events across CAPA · deviations · OOS · audit findings</span>
      </header>
      {q.isLoading && <p className={styles.heatEmpty}>Loading activity…</p>}
      {!q.isLoading && items.length === 0 && (
        <p className={styles.heatEmpty}>No recent activity in the last 24 hours.</p>
      )}
      {items.length > 0 && (
        <ol className={styles.feedList}>
          {items.slice(0, 12).map((item) => (
            <li key={`${item.kind}-${item.id}`} className={styles.feedItem}>
              <Link to={linkFor(item)} className={styles.feedLink}>
                <span className={`${styles.feedKind} ${styles[`kind_${item.kind.toLowerCase()}`]}`}>
                  {KIND_LABEL[item.kind] ?? item.kind}
                </span>
                <span className={styles.feedRef}>{item.ref}</span>
                <span className={styles.feedTitle}>{item.title}</span>
                {item.severity && <span className={styles.feedSeverity}>{item.severity}</span>}
                <span className={styles.feedStatus}>{item.status}</span>
                <span className={styles.feedTime}>{fmtRelative(item.at)}</span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
